import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { formatBrlCurrency } from "@/lib/price";
import type { Product } from "@/types/models";
import { ProductStatusBadge } from "./ProductStatusBadge";
import { ProductThumbnail } from "./ProductThumbnail";

function ProductNameCell({ product }: { product: Product }) {
  return (
    <div className="flex items-center gap-3 min-w-0">
      <ProductThumbnail product={product} />
      <span className="min-w-0 text-primary-900 dark:text-white">{product.name}</span>
    </div>
  );
}

export interface ProductTableProps {
  products: Product[];
  archiving?: boolean;
  deleting?: boolean;
  onArchive: (product: Product) => void;
  onDelete: (product: Product) => void;
  onPrice: (product: Product) => void;
}

function ProductActions({
  product,
  archiving,
  deleting,
  onArchive,
  onDelete,
  onPrice,
}: {
  product: Product;
  archiving: boolean;
  deleting: boolean;
  onArchive: (product: Product) => void;
  onDelete: (product: Product) => void;
  onPrice: (product: Product) => void;
}) {
  return (
    <>
      <Button type="button" variant="outline" size="sm" onClick={() => onPrice(product)}>
        Precificar
      </Button>
      <Button type="button" variant="outline" size="sm" asChild>
        <Link to={`/products/${product.id}/edit`}>Editar</Link>
      </Button>
      {product.status !== "ARCHIVED" && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={archiving}
          onClick={() => onArchive(product)}
        >
          Arquivar
        </Button>
      )}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        disabled={deleting}
        onClick={() => onDelete(product)}
      >
        Excluir
      </Button>
    </>
  );
}

export function ProductTable({
  products,
  archiving = false,
  deleting = false,
  onArchive,
  onDelete,
  onPrice,
}: ProductTableProps) {
  if (products.length === 0) {
    return (
      <section className="border border-primary-200 dark:border-primary-800 panel-padding">
        <p className="text-sm font-light text-primary-600 dark:text-primary-400 mb-6">
          Nenhum produto cadastrado. Crie o primeiro produto para começar.
        </p>
        <Button asChild>
          <Link to="/products/new">Novo produto</Link>
        </Button>
      </section>
    );
  }

  return (
    <section className="border border-primary-200 dark:border-primary-800 overflow-hidden">
      <ul
        className="md:hidden divide-y divide-primary-200 dark:divide-primary-800"
        data-testid="product-table-mobile"
      >
        {products.map((product) => (
          <li key={product.id} className="panel-padding space-y-3">
            <div className="flex items-start justify-between gap-3">
              <ProductNameCell product={product} />
              <ProductStatusBadge status={product.status} />
            </div>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm font-light">
              <div>
                <dt className="text-xs uppercase tracking-widest text-primary-500">Preço</dt>
                <dd className="text-primary-900 dark:text-white">
                  {formatBrlCurrency(product.priceCents)}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-widest text-primary-500">Preço sugerido</dt>
                <dd className="text-primary-900 dark:text-white">
                  {product.suggestedPriceCents != null
                    ? formatBrlCurrency(product.suggestedPriceCents)
                    : "—"}
                </dd>
              </div>
              <div className="col-span-2">
                <dt className="text-xs uppercase tracking-widest text-primary-500">Categoria</dt>
                <dd className="text-primary-600 dark:text-primary-400">
                  {product.category?.name ?? "—"}
                </dd>
              </div>
            </dl>
            <div className="flex flex-wrap gap-2">
              <ProductActions
                product={product}
                archiving={archiving}
                deleting={deleting}
                onArchive={onArchive}
                onDelete={onDelete}
                onPrice={onPrice}
              />
            </div>
          </li>
        ))}
      </ul>

      <div className="hidden md:block overflow-x-auto" data-testid="product-table-desktop">
        <table className="w-full min-w-[48rem] text-sm font-light">
          <thead>
            <tr className="border-b border-primary-200 dark:border-primary-800 text-left text-xs uppercase tracking-widest text-primary-500">
              <th className="px-4 py-4 lg:px-6">Nome</th>
              <th className="px-4 py-4 lg:px-6">Preço</th>
              <th className="px-4 py-4 lg:px-6">Preço sugerido</th>
              <th className="px-4 py-4 lg:px-6">Categoria</th>
              <th className="px-4 py-4 lg:px-6">Status</th>
              <th className="px-4 py-4 lg:px-6 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {products.map((product) => (
              <tr
                key={product.id}
                className="border-b border-primary-100 dark:border-primary-800 last:border-0"
              >
                <td className="px-4 py-4 lg:px-6">
                  <ProductNameCell product={product} />
                </td>
                <td className="px-4 py-4 lg:px-6 text-primary-600 dark:text-primary-400">
                  {formatBrlCurrency(product.priceCents)}
                </td>
                <td className="px-4 py-4 lg:px-6 text-primary-600 dark:text-primary-400">
                  {product.suggestedPriceCents != null
                    ? formatBrlCurrency(product.suggestedPriceCents)
                    : "—"}
                </td>
                <td className="px-4 py-4 lg:px-6 text-primary-600 dark:text-primary-400">
                  {product.category?.name ?? "—"}
                </td>
                <td className="px-4 py-4 lg:px-6">
                  <ProductStatusBadge status={product.status} />
                </td>
                <td className="px-4 py-4 lg:px-6 text-right space-x-2">
                  <ProductActions
                    product={product}
                    archiving={archiving}
                    deleting={deleting}
                    onArchive={onArchive}
                    onDelete={onDelete}
                    onPrice={onPrice}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
